/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useLazyQuery } from '@apollo/client';
import { Badge, Box, Button, Center, Container, Flex, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { faChevronLeft, faChevronRight, faPlus, faSlidersH } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactPaginate from 'react-paginate';
import { useHistory, useLocation } from 'react-router-dom';
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table';
import BreadcrumbComponent from '../../components/BreadcrumbComponent/BreadcrumbComponent';
import CompanyName from '../../components/CompanyName';
import DownloadAndViewButton from '../../components/Files/DownloadAndViewButton';
import ListLoading from '../../components/ListLoading';
import PrintLayout from '../../components/Print/PrintLayout';
import TotalRow from '../../components/TotalRow';
import c from '../../constant';
import useMoment from '../../hooks/useMoment';
import useStore from '../../store';
import {
	annuallyFileReport,
	mdMonthlyClaim,
	mdMonthlyDistribution,
	mdMonthlySale,
	mdQuarterlyClaim,
	mdQuarterlySale,
	monthlyFileReport,
	quarterlyFileReport,
	sdQuarterlyFinance,
	sdQuarterlyInvestment,
	semesterlyFileReport,
} from './Common/Constants';
import { getReportName, getTypeValue, isPermittedUser } from './Common/Utils';
import CreateModal from './Modals/Create';
import DetailModal from './Modals/Detail';
import FilterModal from './Modals/Filter';

const Q_GET = gql`
	query GetReportDue(
		$pagerInput: PagerInput!
		$active: Boolean
		$typeValue: Float
		$fileReportId: String
		$year: Float
		$companyId: String
		$type: ReportDueEnumType
	) {
		getReportDues(
			pagerInput: $pagerInput
			active: $active
			typeValue: $typeValue
			fileReportId: $fileReportId
			year: $year
			companyId: $companyId
			type: $type
		) {
			list {
				id
				createdAt
				creator {
					id
					username
					fullName
					fullNameKhmer
					avatar
				}
				updatedAt
				updater {
					id
					username
					fullName
					fullNameKhmer
					avatar
				}
				type
				year
				typeValue
				dueDate
				company {
					id
					name
					nameKhmer
					type
					logo
				}
				attachment
				active
				fileReport {
					id
					title
					titleKh
					departments
					period
				}
			}
			totalPages
			totalRows
		}
	}
`;

function ReportDue() {
	const toast = useToast();
	const createModal = useDisclosure();
	const filterModal = useDisclosure();
	const location = useLocation();
	const urlParams = new URLSearchParams(location.search);
	const page = urlParams.get('page') ?? 1;
	const reportType = urlParams.get('reportType') ?? null;
	const companyId = urlParams.get('companyId') ?? null;
	const companyType = urlParams.get('companyType') ?? null;
	const year = urlParams.get('year') ?? null;
	const month = urlParams.get('month') ?? null;
	const quarter = urlParams.get('quarter') ?? null;
	const fileReportId = urlParams.get('fileReportId') ?? null;
	const active = urlParams.get('active') ?? null;
	const { toDateString } = useMoment();
	const { push } = useHistory();
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const { currentUser: user } = useStore((state) => state?.currentUser);
	const [detailId, setDetailId] = useState(null);
	const [getReportDues, { loading, data, error }] = useLazyQuery(Q_GET);
	const hasReportDuePermission = useMemo(() => isPermittedUser(user), [user]);

	useEffect(load, [page, reportType, companyId, year, month, quarter, fileReportId, active]);

	useEffect(() => {
		if (error) {
			if (error?.graphQLErrors?.length > 0) {
				error?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					}),
				);
			} else {
				toast({
					title: t(error?.message ? error?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [error]);

	function load() {
		const limit = 10;
		const offset = parseInt(page ? (page - 1) * limit : 0) || 0;
		const pagerInput = { offset: offset < 0 ? 0 : offset, limit };
		let variables = { pagerInput };
		if (reportType) {
			variables = { ...variables, type: reportType };
			if (reportType === mdMonthlySale?.value || reportType === mdMonthlyClaim?.value || reportType === mdMonthlyDistribution?.value) {
				if (month) {
					variables = { ...variables, typeValue: parseInt(month) };
				}
			} else if (
				reportType === mdQuarterlySale?.value ||
				reportType === mdQuarterlyClaim?.value ||
				reportType === sdQuarterlyFinance?.value ||
				reportType === sdQuarterlyInvestment?.value
			) {
				if (quarter) {
					variables = { ...variables, typeValue: parseInt(quarter) };
				}
			} else if (
				reportType === monthlyFileReport?.value ||
				reportType === quarterlyFileReport?.value ||
				reportType === semesterlyFileReport?.value ||
				reportType === annuallyFileReport?.value
			) {
				if (fileReportId) {
					variables = { ...variables, fileReportId };
				}
			}
		}
		if (companyId) {
			variables = { ...variables, companyId };
		}
		if (year) {
			variables = { ...variables, year: parseInt(year) };
		}
		if (active === 'true') {
			variables = { ...variables, active: true };
		}
		getReportDues({ variables });
	}

	function filter(queries) {
		push(`/company-submission-report/report-due${queries}`);
	}

	function handlePageChanged({ selected }) {
		let page = selected + 1;
		let url = '?page=' + page;
		if (reportType) {
			url += `&reportType=${reportType}`;
			if (reportType === mdMonthlySale?.value || reportType === mdMonthlyClaim?.value || reportType === mdMonthlyDistribution?.value) {
				if (month) {
					url += `&month=${month}`;
				}
			} else if (
				reportType === mdQuarterlySale?.value ||
				reportType === mdQuarterlyClaim?.value ||
				reportType === sdQuarterlyFinance?.value ||
				reportType === sdQuarterlyInvestment?.value
			) {
				if (quarter) {
					url += `&quarter=${quarter}`;
				}
			} else if (
				reportType === monthlyFileReport?.value ||
				reportType === quarterlyFileReport?.value ||
				reportType === semesterlyFileReport?.value ||
				reportType === annuallyFileReport?.value
			) {
				if (fileReportId) {
					url += `&fileReportId=${fileReportId}`;
				}
			}
		}
		if (companyType) {
			url += `&companyType=${companyType}`;
		}
		if (companyId) {
			url += `&companyId=${companyId}`;
		}
		if (year) {
			url += `&year=${year}`;
		}
		if (active === true) {
			url += `&active=${true}`;
		}
		push(`/company-submission-report/report-due${url}`);
	}

	return (
		<PrintLayout>
			<>
				<Container maxW="container.xl" mt="16px" mb="16px">
					<BreadcrumbComponent
						list={[
							{
								name: 'Insurance Institute Report',
								path: '/company-submission-report',
							},
							{
								name: 'Permission On Report Due',
								path: '#',
							},
						]}
					/>
					<Center mt="5">
						<Box
							w="100%"
							bg="white"
							borderRadius={c.borderRadius}
							border="1px solid #dbdbdb"
							boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
							pb="32px"
						>
							<Flex
								className="hidden-print responsive-header-for-table"
								bg="#FAFAFA"
								p="16px"
								pt="8px"
								pb="8px"
								mb="16px"
								alignItems="center"
								justifyContent="space-between"
								borderBottom="1px solid #dbdbdb"
							>
								<Text fontSize="x-large" fontWeight="bold">
									{t('Permission On Report Due')}
								</Text>
								{hasReportDuePermission && (
									<Flex>
										<Button
											onMouseDown={filterModal?.onOpen}
											leftIcon={<FontAwesomeIcon icon={faSlidersH} style={{ fontSize: 16 }} />}
											colorScheme={`${reportType || month || quarter || fileReportId || companyId || year || active === 'true' ? 'yellow' : 'gray'}`}
											variant="solid"
											size="sm"
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7}"
										>
											{t('Filter')}
										</Button>
										<Button
											onClick={createModal?.onOpen}
											leftIcon={<FontAwesomeIcon icon={faPlus} style={{ fontSize: 16 }} />}
											colorScheme="gray"
											variant="solid"
											size="sm"
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7"
											ml={2}
										>
											{t('Create')}
										</Button>
									</Flex>
								)}
							</Flex>
							<Box p="16px" pt="0">
								<ListLoading
									loading={loading}
									loadedList={data?.getReportDues?.list}
									totalRows={data?.getReportDues?.totalRows}
									totalPages={data?.getReportDues?.totalPages}
								>
									{({ loadedList, totalPages, totalRows }) => (
										<Box p="16px" pt="8px" pb="8px" mb="16px">
											<Table variant="simple" className="responsive-table-container">
												<Thead>
													<Tr className="td-as-th">
														<Th align="left">{t('Company')}</Th>
														<Th align="left">{t('Type')}</Th>
														<Th align="left">{t('Year')}</Th>
														<Th align="left">{t('Value')}</Th>
														<Th align="left">{t('Due Date')}</Th>
														<Th align="left">{t('Attachment')}</Th>
														<Th align="right">{t('Status')}</Th>
													</Tr>
												</Thead>
												<Tbody>
													{loadedList?.length === 0 && (
														<Tr>
															<Td colSpan={7} textAlign="center">
																{t('No records found')}
															</Td>
														</Tr>
													)}
													{loadedList?.map((e, i) => (
														<Tr _hover={{ bg: 'gray.200' }} key={`tr-${i}`} cursor="pointer" onClick={() => setDetailId(e?.id)}>
															<Td pt={2} pb={2} align="left">
																<CompanyName company={e?.company} />
															</Td>
															<Td pt={2} pb={2} align="left">
																{getReportName(e?.type, lang)}
															</Td>
															<Td pt={2} pb={2} align="left">
																{e?.year ?? 'N/A'}
															</Td>
															<Td pt={2} pb={2} align="left">
																{getTypeValue(e?.type, e?.typeValue, e?.fileReport, lang, t)}
															</Td>
															<Td pt={2} pb={2} align="left">
																{toDateString(e?.dueDate, c.dateTimeFormat)}
															</Td>
															<Td pt={2} pb={2} align="left">
																<DownloadAndViewButton
																	marginLeft={0}
																	url={e?.attachment?.file}
																	size={e?.attachment?.fileSize}
																/>
															</Td>
															<Td pt={2} pb={2} align="right">
																<Flex justifyContent="flex-end" alignItems="center">
																	<Badge colorScheme={e?.active === true ? 'green' : 'red'}>
																		{e?.active === true ? t('Active') : t('Inactive')}
																	</Badge>
																</Flex>
															</Td>
														</Tr>
													))}
												</Tbody>
												<tfoot className="responsive-footer">
													<Tr className="td-as-th">
														<Th align="left">{t('Company')}</Th>
														<Th align="left">{t('Type')}</Th>
														<Th align="left">{t('Year')}</Th>
														<Th align="left">{t('Value')}</Th>
														<Th align="left">{t('Due Date')}</Th>
														<Th align="left">{t('Attachment')}</Th>
														<Th align="right">{t('Status')}</Th>
													</Tr>
												</tfoot>
											</Table>
											{totalRows > 0 && (
												<Flex justifyContent="flex-end" direction="row">
													<TotalRow count={totalRows} mt={2} />
												</Flex>
											)}
											{totalPages > 1 && (
												<Flex alignItems="flex-end" direction="column" mt={4}>
													<ReactPaginate
														previousLabel={<FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: 12, cursor: 'pointer' }} />}
														nextLabel={<FontAwesomeIcon icon={faChevronRight} style={{ fontSize: 12, cursor: 'pointer' }} />}
														breakLabel={'...'}
														breakClassName={'break-me'}
														disableInitialCallback={true}
														pageCount={totalPages}
														marginPagesDisplayed={2}
														pageRangeDisplayed={3}
														onPageChange={handlePageChanged}
														containerClassName={'pagination'}
														activeClassName={'active'}
														initialPage={(page - 1) | 0}
													/>
												</Flex>
											)}
										</Box>
									)}
								</ListLoading>
							</Box>
						</Box>
					</Center>
				</Container>
				<>{createModal?.isOpen && <CreateModal onClose={createModal?.onClose} onComplete={load} />}</>
				<>
					{filterModal?.isOpen && (
						<FilterModal
							reportType={reportType}
							companyId={companyId}
							companyType={companyType}
							year={year}
							month={month}
							quarter={quarter}
							fileReportId={fileReportId}
							active={active === 'true' ? true : false}
							onClose={filterModal?.onClose}
							onApply={filter}
						/>
					)}
				</>
				<>{detailId && <DetailModal id={detailId} onClose={() => setDetailId(null)} onComplete={load} />}</>
			</>
		</PrintLayout>
	);
}

export default ReportDue;
